function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import isNumber from 'lodash/isNumber';
import throttle from 'lodash/throttle';
import clsx from 'clsx';
import { withStyles } from 'common/util/react';
import { TOOLTIP_THROTTLE_TIME } from 'common/components/chart-tooltip/util';
import { getSeriesMatchPath } from 'common/charts/contexts/utils';
import { useSegmentFilterController } from 'common/contexts/react/segment-filter-controller-context';
import { ChartTooltip, ChartTooltipJsxProps } from 'common/components/chart-tooltip';
import styles from './index.module.styl';
import css from './index.module.styl?inline';
import { MetricChartReact } from './metric';
import { MetricChartSize, MetricSegment, MultiMetricChartProps } from './types';
import { getGridDimensions } from './utils';
const NUM_CENTERED_METRICS = 6;
export const MultiMetricChart: React.FC<MultiMetricChartProps> = props => {
  const [selfHeight, setSelfHeight] = useState(0);
  const [selfWidth, setSelfWidth] = useState(0);
  const [hoverTooltip, setHoverTooltip] = useState<ChartTooltipJsxProps | null>(null);
  const rootRef = useRef(null);
  const onMetricMouseMove = (ev: MouseEvent, metricSegment: MetricSegment) => {
    ev.stopPropagation();
    const {
      clientX,
      clientY
    } = ev;
    if (!props.tooltipDataCallback || props.selectedMetric) {
      return;
    }
    const tooltipParams = props.tooltipDataCallback(metricSegment);
    props.segmentFilterController.notifySeriesHovered({
      seriesId: metricSegment.path.join(",")
    });
    setHoverTooltip({
      props: _objectSpread(_objectSpread({}, tooltipParams), {}, {
        positioning: {
          clientX,
          clientY
        },
        renderInPortal: true
      })
    });
  };
  const onMetricMouseLeave = () => {
    props.segmentFilterController.notifySeriesHovered(null);
    setHoverTooltip(null);
  };
  const formatMetric = (segment: MetricSegment, value: number): string => {
    return props.metricFormatter(segment, value);
  };
  useEffect(() => {
    const resizeObserver = new ResizeObserver(entries => {
      const {
        width,
        height
      } = entries[0].contentRect;
      setSelfHeight(height);
      setSelfWidth(width);
    });
    resizeObserver.observe(rootRef.current);
    return () => {
      resizeObserver.disconnect();
    };
  }, [rootRef]);
  useEffect(() => {
    if (props.selectedMetric) {
      setHoverTooltip(null);
    }
  }, [props.selectedMetric]);
  const getMultiMetricGridDimensions = () => {
    return getGridDimensions(selfWidth, selfHeight, props.metrics.length);
  };
  const getMetricSize = () => {
    if (props.metricSize) {
      return props.metricSize;
    }

    // adjust as needed for different metric sizes
    const minHeightForLargeSize = 200;
    const minWidthForLargeSize = 256;
    const {
      rows,
      columns
    } = getMultiMetricGridDimensions();
    return selfHeight / rows >= minHeightForLargeSize && selfWidth / columns >= minWidthForLargeSize ? MetricChartSize.Large : MetricChartSize.Small;
  };
  const getMetricsToDisplay = (): {
    gridMetrics: MetricSegment[];
    bottomMetrics: MetricSegment[];
  } => {
    const metrics = props.metrics;
    const {
      rows,
      columns
    } = getMultiMetricGridDimensions();

    // only show as many metrics as allowed by the grid
    const maxAllowed = rows * columns;
    const truncatedMetrics = metrics.slice(0, maxAllowed);

    // render bottom row as separate grid in cases that there is an odd number of metrics
    // this allows us to fill up the remaining space
    const numBottomMetrics = maxAllowed === truncatedMetrics.length ? columns : truncatedMetrics.length - columns * (rows - 1);
    const [gridMetrics, bottomMetrics] = [truncatedMetrics.slice(0, truncatedMetrics.length - numBottomMetrics), truncatedMetrics.slice(truncatedMetrics.length - numBottomMetrics)];
    return {
      gridMetrics,
      bottomMetrics
    };
  };
  const metrics = props.metrics;
  if (!metrics.length) {
    return <div className={styles.root} />;
  }
  const {
    rows,
    columns
  } = getMultiMetricGridDimensions();
  const {
    gridMetrics,
    bottomMetrics
  } = getMetricsToDisplay();
  const metricSize = getMetricSize();
  const isCentered = metrics.length <= NUM_CENTERED_METRICS;
  const hoveredSeries = props.segmentFilterController.hoveredSeries;
  const renderMetricChart = (_ref: MetricSegment, idx, numMetrics) => {
    var _props$selectedMetric;
    let {
      comparisonValue,
      label,
      value,
      secondaryValue,
      path,
      stepIndex,
      subLabel,
      color,
      liftComparisonValue
    }: MetricSegment = _ref;
    const segment = _objectSpread({
      value,
      secondaryValue,
      label,
      path,
      color,
      comparisonValue
    }, {
      stepIndex
    }) as MetricSegment;
    const formatted = formatMetric(segment, value);
    let valueTooltip = "";
    let liftFormattedCompare;
    if (props.liftType && isNumber(comparisonValue)) {
      // lift CTP and Baseline have extra numerical values to display
      valueTooltip += " / ".concat(formatMetric(segment, comparisonValue));
      liftFormattedCompare = formatMetric(segment, comparisonValue);
    }
    const isLastInRow = (idx + 1) % columns === numMetrics % columns;
    const enableClick = !!props.onMetricChartClick;
    let isVirtuallyHovered = false;
    const metricId = path === null || path === void 0 ? void 0 : path.join(",");
    if (hoveredSeries !== null && hoveredSeries !== void 0 && hoveredSeries.seriesId && hoveredSeries !== null && hoveredSeries !== void 0 && hoveredSeries.partialMatch) {
      isVirtuallyHovered = getSeriesMatchPath(metricId) === getSeriesMatchPath(hoveredSeries.seriesId);
    } else if (hoveredSeries !== null && hoveredSeries !== void 0 && hoveredSeries.seriesId) {
      isVirtuallyHovered = metricId === hoveredSeries.seriesId;
    }
    return <div elref={"metricContainer"} key={"".concat(idx, "-").concat(label)} className={clsx(styles.metricContainer, {
      [styles.firstInRow]: idx === 0,
      [styles.lastInRow]: isLastInRow,
      [styles.selected]: ((_props$selectedMetric = props.selectedMetric) === null || _props$selectedMetric === void 0 ? void 0 : _props$selectedMetric.label) === label,
      [styles.virtualHover]: isVirtuallyHovered,
      [styles.clickable]: enableClick
    })} {...enableClick ? {
      tabIndex: 0
    } : {}} onMouseMove={props.tooltipDataCallback && throttle(ev => onMetricMouseMove(ev, segment), TOOLTIP_THROTTLE_TIME, {
      leading: true,
      trailing: false
    })} onMouseLeave={props.tooltipDataCallback && (() => onMetricMouseLeave())} onClick={enableClick ? ev => props.onMetricChartClick(ev, segment) : () => {}} data-sentry-component="renderMetricChart" data-sentry-source-file="index.tsx">
        <MetricChartReact displayValue={formatted} valueTooltip={valueTooltip} comparisonValue={comparisonValue} subLabel={subLabel} isCentered={isCentered} hideTooltip={!!props.tooltipDataCallback} hideValue={props.hideValue} hideSubValue={props.hideSubValue} size={metricSize} label={label} value={value} metricId={metricId} color={color} liftContext={props.liftType ? {
        liftType: props.liftType,
        liftComparisonValue,
        comparisonValue: liftFormattedCompare
      } : undefined} data-sentry-element="MetricChartReact" data-sentry-source-file="index.tsx" />
      </div>;
  };
  return <div className={styles.root} ref={rootRef} data-sentry-component="MultiMetricChart" data-sentry-source-file="index.tsx">
      <div className={styles.grid} style={{
      gridTemplateColumns: "repeat(".concat(columns, ", 1fr)"),
      gridTemplateRows: "repeat(".concat(rows, ", 1fr)")
    }}>
        {gridMetrics.map((metric, idx) => renderMetricChart(metric, idx, gridMetrics.length))}
        {bottomMetrics.length ? <div className={styles.bottomRow} elref={"bottomRow"} style={{
        gridTemplateColumns: "repeat(".concat(bottomMetrics.length, ", 1fr)")
      }}>
            {bottomMetrics.map((metric, idx) => renderMetricChart(metric, idx, bottomMetrics.length))}
          </div> : null}
      </div>
      {hoverTooltip && <ChartTooltip {...hoverTooltip.props} />}
    </div>;
};
export const MultiMetricChartReact = withStyles((props: MultiMetricChartProps) => {
  const segmentFilterController = useSegmentFilterController();
  return <MultiMetricChart segmentFilterController={segmentFilterController} {...props} />;
}, css);